"use client";

import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { CheckIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import Button from "../../components/Button";
import useApi from "../../hooks/useApi";
import { useNotifications } from "../../contexts/notificationContext";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useCompany } from "../../contexts/companyContext";

export default function CustomEmailSetupModal(props: { open: boolean; setOpen: (value: boolean) => void }) {
  const { open, setOpen } = props;
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { postApiData } = useApi();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const { companyId } = useCompany();

  async function sendVerification() {
    setSubmitting(true);

    if (!email) {
      setError("Email is required");
      setSubmitting(false);
      return;
    }

    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Email is not valid");
      setSubmitting(false);
      return;
    }

    try {
      const { response, body } = await postApiData("/company/email/send-identify-verification", {
        email,
      });

      if (response.ok) {
        await queryClient.invalidateQueries({ queryKey: ["emailIdentity", companyId] });
        notifications.addNotification("Verification Sent!", "Please check your email for the verification link");
        setOpen(false);
        setEmail("");
        setError("");
      } else {
        notifications.addNotification("Error", "An unexpected error occured, please try again later", "error");
      }
    } catch (err) {
      console.log(err);
      notifications.addNotification("Error", "An unexpected error occured, please try again later", "error");
    }
    setSubmitting(false);
  }

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-xl sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="px-4 sm:px-0">
              <h3 className="font-semibold leading-6 text-gray-900">Custom Assessment Email</h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                Enter the email you want the assessment to be sent from. Choose an email that you have access to the inbox, as you will required to verify the email.
              </p>
            </div>
            <div className="sm:col-span-3 mt-3">
              <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                Email
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  type="text"
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-blue-600 sm:text-sm/6"
                />
              </div>
            </div>
            <div className="text-sm text-red-500 mt-2">{error}</div>

            <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <Button text="Cancel" variant="secondary" onClick={() => setOpen(false)} />
              <Button text="Send Verification Email" icon={PaperAirplaneIcon} type="button" onClick={() => sendVerification()} submitting={submitting} />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
